import {
  FieldLabel,
  getFontCss,
  HoverableLink,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import Box from 'src/components/Box/Box'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import SvgUpdate from 'src/components/Svgs/SvgUpdate'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import TopLevelError from 'src/components/TopLevelError/TopLevelError'
import {
  FormFieldsValidation,
  useFormFields,
  useScrollToScrollGroupRefs,
} from 'src/hooks'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

export type AccountInformationFlatFields = {
  displayName: string
  companyName: string
}

interface AccountInformationBoxProps {
  className?: string
  hasEditPermissions: boolean
  creditCard: CreditCardListModel
  puttingCreditCard: boolean
  puttingCreditCardAccount: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  onSuccessfulSubmit: (fields: AccountInformationFlatFields) => void
  inFocus: boolean
}

const AccountInformationBox: React.FC<AccountInformationBoxProps> = (
  props,
): React.ReactElement => {
  const locale = useSelector(selectNullSafeLocaleFromState)

  const [isEditing, setIsEditing] = useState<boolean>(false)

  const initialFlatFields: AccountInformationFlatFields = {
    displayName: props.creditCard.displayName,
    companyName: props.creditCard.companyName,
  }
  const flatFieldsValidation =
    (): FormFieldsValidation<AccountInformationFlatFields> => ({
      displayName: [stringIsEmpty()],
      companyName: [stringIsEmpty()],
    })
  const {
    fields: flatFields,
    setFields: setFlatFields,
    fieldsErrors: flatFieldsErrors,
    onFieldChange: onFlatFieldChange,
    setErrorsAndReturnDidPassValidation: flatFieldsSetErrors,
    topmostScrollGroup: flatFieldsTopmostScrollGroup,
  } = useFormFields<AccountInformationFlatFields>(
    initialFlatFields,
    flatFieldsValidation,
    props.errorDetailMap,
  )

  const scrollRef0 = useRef<HTMLFormElement>(null)
  useScrollToScrollGroupRefs([scrollRef0], [flatFieldsTopmostScrollGroup])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()

    const flatFieldsPassedValidation = flatFieldsSetErrors()
    if (flatFieldsPassedValidation) {
      props.onSuccessfulSubmit(flatFields)
    }
  }

  const isDirty = useMemo(
    () =>
      flatFields.displayName !== props.creditCard.displayName ||
      flatFields.companyName !== props.creditCard.companyName,
    [flatFields, props.creditCard],
  )

  return (
    <Box
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
        `}
      >
        <span
          css={css`
            ${getFontCss(
              20,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )}
          `}
        >
          <Trns source={'Account Information'} />
        </span>
        {props.hasEditPermissions &&
          !isEditing &&
          !props.creditCard.isClosed && (
            <HoverableLink
              css={css`
                display: flex;
                align-items: center;
              `}
              onClick={(): void => setIsEditing(true)}
              ariaLabelSource={`Update`}
            >
              <Trns source={'Update'} />
              <SvgUpdate
                css={css`
                  transform: scale(calc(7 / 12));
                `}
              />
            </HoverableLink>
          )}
      </div>
      <form
        ref={scrollRef0}
        onSubmit={onSubmit}
        css={css`
          display: flex;
          flex-direction: column;
          margin-top: 40px;
        `}
      >
        <FieldLabel>
          <Trns source={'Account Display Name'} />
        </FieldLabel>
        {isEditing ? (
          <TextInputField
            errors={flatFieldsErrors['displayName']}
            design={TextInputFieldDesigns.New}
            value={flatFields['displayName']}
            showUpdates
            baseValue={props.creditCard['displayName']}
            onChange={(val: string): void => {
              onFlatFieldChange('displayName', val)
            }}
            placeholder={translateSingleString(locale, 'Enter ${fieldLabel}', {
              fieldLabel: translateSingleString(locale, 'Account Display Name'),
            })}
          />
        ) : (
          <span>{props.creditCard['displayName']}</span>
        )}
        <FieldLabel
          css={css`
            margin-top: 30px;
          `}
        >
          <Trns source={'Company Name'} />
        </FieldLabel>
        <span>{props.creditCard['companyName']}</span>
        {isEditing && (
          <div
            css={css`
              margin-top: 40px;
              display: flex;
              flex-direction: row;
              gap: 20px;
              align-items: center;
            `}
          >
            <Button
              type={ButtonTypes.submit}
              design={ButtonDesigns.designCta}
              text={<Trns source={'Save Changes'} />}
              loading={props.puttingCreditCardAccount}
              disabled={!isDirty || props.puttingCreditCard}
            />
            <HoverableLink
              onClick={(): void => {
                setFlatFields(initialFlatFields)
                setIsEditing(false)
              }}
              ariaLabelSource={'Cancel'}
            >
              <Trns source={'Cancel'} />
            </HoverableLink>
          </div>
        )}
        {props.topLevelError !== undefined && props.inFocus && (
          <TopLevelError
            css={css`
              align-self: center;
              margin-top: 20px;
            `}
            error={props.topLevelError}
          />
        )}
      </form>
    </Box>
  )
}

export default AccountInformationBox
